import { getRequest, postRequest } from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';
import type { CleaningReport, FloorPlanSuggestions, FloorPlanValidation } from '../models/floorplan-automation';

/**
 * The automation endpoints.
 *
 * Apart from the editor's own service because these answer questions about what is *stored*
 * rather than about what is on screen — whether a desk has a Workplaces row behind it, what
 * the drawing contains that nobody has registered — and because two of the three are only
 * ever read.
 */

/** Why a call did not answer. Kept apart from success so a panel cannot render "ok" as a fault. */
export type AutomationFailure = 'forbidden' | 'failed';

export type AutomationStatus = 'ok' | AutomationFailure;

export interface AutomationResult<T> {
  readonly status: AutomationStatus;
  readonly data?: T;
}

/**
 * 401 and 403 are the same thing to the admin in front of it: these endpoints need
 * administrator rights on this particular office, which the realm role that opened the
 * editor does not by itself grant.
 */
const statusOf = (status: number): AutomationFailure => (
  status === 401 || status === 403 ? 'forbidden' : 'failed'
);

export const getFloorPlanValidation = async (
  floorPlanId: string,
  keycloak: KeycloakContext,
): Promise<AutomationResult<FloorPlanValidation>> => {
  const response = await getRequest<FloorPlanValidation>(`floorplan/${floorPlanId}/validation`, keycloak);

  if (response.error || !response.json) {
    return { status: statusOf(response.status) };
  }

  return { status: 'ok', data: response.json };
};

export interface SuggestionOptions {
  readonly deskWidthMetres?: number;
  readonly deskDepthMetres?: number;
  readonly minimumRoomSquareMetres?: number;
  readonly maximumRoomSquareMetres?: number;
}

export const getFloorPlanSuggestions = async (
  floorPlanId: string,
  keycloak: KeycloakContext,
  options: SuggestionOptions = {},
): Promise<AutomationResult<FloorPlanSuggestions>> => {
  // Omitted rather than sent empty, so the server falls back to its own defaults instead of
  // being asked for desks nought metres wide.
  const query = Object.entries(options)
    .filter(([, value]) => typeof value === 'number' && Number.isFinite(value))
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  const response = await getRequest<FloorPlanSuggestions>(
    `floorplan/${floorPlanId}/suggestions${query ? `?${query}` : ''}`,
    keycloak,
  );

  if (response.error || !response.json) {
    return { status: statusOf(response.status) };
  }

  return { status: 'ok', data: response.json };
};

/**
 * Tidies the stored metadata, or reports what tidying would do.
 *
 * The one automation that writes on the server. `dryRun` defaults to true on both sides:
 * this rewrites stored bytes on a plan that is live in an app, so the safe answer is the one
 * you get by forgetting to pass anything.
 */
export const cleanFloorPlanMetadata = async (
  floorPlanId: string,
  keycloak: KeycloakContext,
  dryRun = true,
): Promise<AutomationResult<CleaningReport>> => {
  const response = await postRequest<CleaningReport>(
    `floorplan/${floorPlanId}/clean-metadata?dryRun=${dryRun}`,
    keycloak,
    {},
  );

  if (response.error || !response.json) {
    return { status: statusOf(response.status) };
  }

  return { status: 'ok', data: response.json };
};
